/**
 * =====================================================================
 * MODO DEMONSTRAÇÃO
 * =====================================================================
 * Serve para avaliar layout, cores e navegação SEM precisar do Supabase
 * configurado. Tudo aqui é fictício e vive só na memória do navegador:
 * ao recarregar a página, volta ao estado inicial.
 *
 * Como ligar:  http://localhost:5173/?demo
 * Como sair:   botão "Sair da demonstração" na barra amarela do topo.
 *
 * Esta pasta inteira pode ser apagada quando o sistema estiver em uso.
 * As chamadas que a consultam estão marcadas com o comentário
 * "guarda do modo demonstração" nos arquivos de src/data/.
 * =====================================================================
 */

const CHAVE = 'tarter_modo_demo';

function detectar() {
  try {
    const params = new URLSearchParams(location.search);
    if (params.has('demo')) {
      sessionStorage.setItem(CHAVE, '1');
      return true;
    }
    return sessionStorage.getItem(CHAVE) === '1';
  } catch {
    return new URLSearchParams(location.search).has('demo');
  }
}

export const emDemonstracao = detectar();

export function sairDaDemonstracao() {
  try { sessionStorage.removeItem(CHAVE); } catch { /* ignora */ }
  location.href = location.pathname;
}

/* ------------------------------------------------------------------ */
/* Pessoas                                                             */
/* ------------------------------------------------------------------ */

export const colaboradores = [
  { id: 'c-beatriz', nome: 'Beatriz', email: 'beatriz@tarter.com.br', papel: 'gestao', departamento: 'contabil', ativo: true },
  { id: 'c-ademir', nome: 'Ademir', email: 'ademir@tarter.com.br', papel: 'colaborador', departamento: 'contabil', ativo: true },
  { id: 'c-cleytson', nome: 'Cleytson', email: 'cleytson@tarter.com.br', papel: 'colaborador', departamento: 'contabil', ativo: true },
];

/** Entra como Beatriz, para a demonstração já mostrar a visão de gestão. */
export const usuarioDemo = colaboradores[0];

const ref = (id) => {
  const c = colaboradores.find((x) => x.id === id);
  return { id: c.id, nome: c.nome };
};

/* ------------------------------------------------------------------ */
/* Checklist padrão — o mesmo do manual (9 etapas, 26 itens)           */
/* ------------------------------------------------------------------ */

const ETAPAS = [
  ['Integrações com o Sistema Domínio', [
    'Importar Extratos ou Parametrizar a Contabilidade Digital',
    'Integrar Fiscal — Conferir resumo por acumulador e salvar na pasta',
    'Integrar Folha']],
  ['Lançamentos', [
    'Lançar despesas pagas via banco após importação dos extratos bancários',
    'Lançar despesas pagas pelo caixinha',
    'Lançar rendimentos de aplicações financeiras, IR e IOF']],
  ['Salvar as consultas na pasta', [
    'Consulta de pagamentos do ECAC',
    'Relatório de situação fiscal do ECAC',
    'Consulta do DUC',
    'Consulta da Sefaz em caso de comércio']],
  ['Ativo Imobilizado e Depreciação (Domínio Patrimônio)', [
    'Lançar cadastro de bens do ativo imobilizado (em caso de compra)',
    'Integrar e/ou lançar a depreciação acumulada mensalmente',
    'Conferir saldo de depreciação acumulada com o relatório Mapa de Depreciação Sintético']],
  ['Seguros e Empréstimos', [
    'Lançar apropriações de contratados mensalmente']],
  ['Composição de Saldos e Lançamentos Contábeis', [
    'Lançar na planilha de Composição de Saldos os valores das contas em aberto',
    'Conferir se as classificações das despesas foram contabilizadas corretamente',
    'Conferir e conciliar todas as contas de Ativo, Passivo e Resultado',
    'Verificar se os valores da planilha de Composição de Saldos coincidem com o balancete']],
  ['Consulta contratos na pasta de legalização', [
    'Verificar se ocorreu alteração no contrato social',
    'Verificar se houve alteração no capital social da empresa no exercício',
    'Verificar se houve alteração no quadro societário da empresa no exercício']],
  ['Resultado do Período', [
    'Verificar se a empresa apresenta lucro ou prejuízo no período',
    'Verificar se DRE e Balancete refletem corretamente o resultado']],
  ['Finalização e Arquivamento', [
    'Salvar balancete em PDF na pasta do cliente',
    'Salvar DRE em PDF na pasta do cliente',
    'Atualizar a planilha de controle contábil CON01']],
];

let proximoItemId = 1;
export const checklist = ETAPAS.map(([titulo, textos], i) => ({
  id: i + 1,
  ordem: i + 1,
  titulo,
  itens: textos.map((texto, j) => ({ id: proximoItemId++, ordem: j + 1, texto, ativo: true })),
}));

export const totalChecklist = checklist.reduce((n, e) => n + e.itens.length, 0);

/* ------------------------------------------------------------------ */
/* Empresas (fictícias)                                                */
/* ------------------------------------------------------------------ */

const BANCO_CODIGO = {
  'Itaú': '341', 'Bradesco': '237', 'Sicoob': '756', 'Nubank': '260',
  'Santander': '033', 'Caixa': '104', 'Banco do Brasil': '001',
};

let seqConta = 1000;
function banco(empresaId, nome, openFinance = true) {
  const n = seqConta++;
  return {
    id: `${empresaId}-${nome.toLowerCase().replace(/\W/g, '')}`,
    nome,
    instituicao: nome.toUpperCase() + ' EMPRESAS',
    banco_codigo: BANCO_CODIGO[nome] || '000',
    agencia: String(n).padStart(4, '0'),
    conta: String(n * 7).slice(0, 5) + '-' + (n % 10),
    open_finance: openFinance,
    ativo: true,
  };
}

/**
 * Regras por instituição — espelho da tabela regras_banco.
 * É regra do banco, não do cliente: vale para toda conta daquela instituição.
 */
export const regrasBanco = [
  {
    banco_codigo: '341',
    instituicao: 'Itaú',
    regra: 'Só é válido o PDF consolidado. Extrato por conta separada não serve.',
    severidade: 'bloqueio',
  },
];

export function regraDoBanco(b) {
  return regrasBanco.find((r) =>
    (r.banco_codigo && r.banco_codigo === b.banco_codigo)
    || String(b.nome || '').toLowerCase().includes(r.instituicao.toLowerCase())
  ) || null;
}

export const empresas = [
  {
    id: 'e-rosa', codigo: '0142', nome: 'Metalúrgica Rosa Ltda', cnpj: '12.345.678/0001-90',
    regime: 'Lucro Presumido', periodicidade: 'mensal', zappy: 'Metalúrgica Rosa',
    email: 'financeiro@rosametal.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 3, aplicacoes: true,
    contas_no_open: 'Itaú CC, Bradesco CC', contas_fora_open: 'Sicoob CC (aguardando adesão)',
    situacao_doc: 'Pendente', ultima_cobranca: '2026-08-12',
    andamento: 'Documentos pendentes travando o fechamento', status_atual: 'Aguardando cliente',
    particularidades: 'Sócio administrador prefere contato só por e-mail, nunca por telefone. Empresa tem CNPJ filial em SP que não movimenta — conferir todo fechamento.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-ademir')],
    bancos: [banco('e-rosa', 'Itaú'), banco('e-rosa', 'Bradesco'), banco('e-rosa', 'Sicoob', false)],
  },
  {
    id: 'e-andrade', codigo: '0198', nome: 'Comercial Andrade ME', cnpj: '23.456.789/0001-11',
    regime: 'Simples Nacional', periodicidade: 'mensal', zappy: 'Comercial Andrade',
    email: 'andrade@andrademe.com.br',
    funcionarios: false, folha_conosco: false, pro_labore_somente: true,
    open_finance: true, layout: 'OFX', qtd_contas: 1, aplicacoes: false,
    contas_no_open: 'BB CC', contas_fora_open: '—',
    situacao_doc: 'Em dia', ultima_cobranca: null,
    andamento: 'Fechamento em dia', status_atual: 'Concluído',
    particularidades: 'Sem particularidades registradas.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-cleytson')],
    bancos: [banco('e-andrade', 'Banco do Brasil')],
  },
  {
    id: 'e-vitalle', codigo: '0210', nome: 'Grupo Vitalle Saúde', cnpj: '34.567.890/0001-22',
    regime: 'Lucro Real', periodicidade: 'trimestral', zappy: 'Vitalle Saúde',
    email: 'contabil@vitallesaude.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 2, aplicacoes: true,
    contas_no_open: 'Itaú CC, Santander CC', contas_fora_open: '—',
    situacao_doc: 'Em dia', ultima_cobranca: null,
    andamento: 'Fechamento em dia', status_atual: 'Concluído',
    particularidades: 'Empresa com apuração de Lucro Real trimestral — atenção ao LALUR a cada trimestre.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-beatriz'), ref('c-cleytson')],
    bancos: [banco('e-vitalle', 'Itaú'), banco('e-vitalle', 'Santander')],
  },
  {
    id: 'e-novorumo', codigo: '0233', nome: 'Novo Rumo Distribuidora', cnpj: '45.678.901/0001-33',
    regime: 'Simples Nacional', periodicidade: 'mensal', zappy: 'Novo Rumo',
    email: 'financeiro@novorumo.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 2, aplicacoes: false,
    contas_no_open: 'Itaú CC', contas_fora_open: 'Sicoob CC',
    situacao_doc: 'Pendente', ultima_cobranca: '2026-09-03',
    andamento: 'Aguardando extrato Sicoob para fechar Julho', status_atual: 'Aguardando cliente',
    particularidades: 'Cliente costuma atrasar o envio do extrato Sicoob — cobrar com antecedência.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-ademir')],
    bancos: [banco('e-novorumo', 'Itaú'), banco('e-novorumo', 'Sicoob', false)],
  },
  {
    id: 'e-navegar', codigo: '0257', nome: 'Navegar Turismo Ltda', cnpj: '56.789.012/0001-44',
    regime: 'Lucro Presumido', periodicidade: 'mensal', zappy: 'Navegar Turismo',
    email: 'financeiro@navegarturismo.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 2, aplicacoes: false,
    contas_no_open: 'Bradesco CC', contas_fora_open: 'Caixa CC (Open incompleto)',
    situacao_doc: 'Em análise', ultima_cobranca: '2026-09-03',
    andamento: 'Open Finance da Caixa incompleto — reconfigurar adesão', status_atual: 'Em andamento',
    particularidades: 'Conta Caixa foi aberta recentemente — Open ainda não homologado pelo banco.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-cleytson')],
    bancos: [banco('e-navegar', 'Bradesco'), banco('e-navegar', 'Caixa', false)],
  },
  {
    id: 'e-farias', codigo: '0281', nome: 'Farias Engenharia', cnpj: '67.890.123/0001-55',
    regime: 'Lucro Real', periodicidade: 'mensal', zappy: 'Farias Engenharia',
    email: 'contabil@fariasengenharia.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 3, aplicacoes: true,
    contas_no_open: 'Itaú CC, BB CC, Bradesco CC', contas_fora_open: '—',
    situacao_doc: 'Em dia', ultima_cobranca: null,
    andamento: 'Conciliação bancária pendente — 4 lançamentos sem identificação', status_atual: 'Em andamento',
    particularidades: 'Empresa com muitos recebimentos por obra — sempre conferir centro de custo antes de conciliar.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-beatriz'), ref('c-ademir')],
    bancos: [banco('e-farias', 'Itaú'), banco('e-farias', 'Banco do Brasil'), banco('e-farias', 'Bradesco')],
  },
  {
    id: 'e-cardoso', codigo: '0305', nome: 'Cardoso de Mello Advocacia', cnpj: '78.901.234/0001-66',
    regime: 'Lucro Presumido', periodicidade: 'mensal', zappy: 'Cardoso de Mello',
    email: 'financeiro@cmadvocacia.com.br',
    funcionarios: true, folha_conosco: true, pro_labore_somente: false,
    open_finance: true, layout: 'CNAB240', qtd_contas: 1, aplicacoes: true,
    contas_no_open: 'Itaú CC', contas_fora_open: '—',
    situacao_doc: 'Em dia', ultima_cobranca: null,
    andamento: 'Conciliação de honorários em andamento', status_atual: 'Em andamento',
    particularidades: 'Cliente migrou de outro escritório em 07/2025 — validar sempre saldos de abertura antes de ajustes.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-beatriz')],
    bancos: [banco('e-cardoso', 'Itaú')],
  },
  {
    id: 'e-estetica', codigo: '0318', nome: 'Vitalle Estética EIRELI', cnpj: '89.012.345/0001-77',
    regime: 'Simples Nacional', periodicidade: 'mensal', zappy: 'Vitalle Estética',
    email: 'financeiro@vitalleestetica.com.br',
    funcionarios: false, folha_conosco: false, pro_labore_somente: true,
    open_finance: true, layout: 'OFX', qtd_contas: 1, aplicacoes: false,
    contas_no_open: 'Nubank CC', contas_fora_open: '—',
    situacao_doc: 'Em dia', ultima_cobranca: null,
    andamento: 'Classificação de despesas do mês em andamento', status_atual: 'Em andamento',
    particularidades: 'Sem particularidades registradas.',
    situacao: 'ativo', cliente_desde: '2025-01-01', cliente_ate: null, motivo_saida: null, responsaveis: [ref('c-cleytson')],
    bancos: [banco('e-estetica', 'Nubank')],
  },
];

/* Os três estados aparecem na demonstração: ativo, sem movimento e
   ex-cliente. O ex-cliente é o caso que a Beatriz descreveu — foi cliente
   de janeiro a março e depois saiu. */
empresas.find((e) => e.id === 'e-estetica').situacao = 'sem_movimento';

empresas.push({
  id: 'e-saopedro', codigo: '0355', nome: 'São Pedro Comércio ME', cnpj: '90.123.456/0001-88',
  regime: 'Simples Nacional', periodicidade: 'mensal', zappy: 'São Pedro',
  email: 'contato@saopedrome.com.br',
  funcionarios: false, folha_conosco: false, pro_labore_somente: true,
  open_finance: false, layout: '—', qtd_contas: 1, aplicacoes: false,
  contas_no_open: '—', contas_fora_open: 'Bradesco CC',
  situacao_doc: 'Em dia', ultima_cobranca: null,
  andamento: 'Contrato encerrado em março.', status_atual: 'Encerrado',
  particularidades: 'Migrou para escritório próprio do sócio.',
  situacao: 'encerrado', cliente_desde: '2026-01-01', cliente_ate: '2026-03-31',
  motivo_saida: 'Migrou de escritório',
  responsaveis: [ref('c-cleytson')],
  bancos: [banco('e-saopedro', 'Bradesco')],
});

/* ------------------------------------------------------------------ */
/* Fechamentos, itens e marcações                                      */
/* ------------------------------------------------------------------ */

let seq = 1;
const novoId = (p) => `${p}-${seq++}`;

export const fechamentos = [];
export const itens = [];
export const marcacoes = [];
export const historico = [];
export const documentos = [];
export const comentarios = [];
export const cobrancas = [];

/** Cria competências e já deixa algumas parcialmente feitas. */
function semear(empresaId, meses, config) {
  meses.forEach((competencia, indice) => {
    const ultimo = indice === meses.length - 1;
    const f = {
      id: novoId('f'),
      empresa_id: empresaId,
      competencia,
      status: ultimo ? config.statusFinal : 'concluido',
      concluido_em: ultimo && config.statusFinal === 'concluido' ? '2026-09-10T14:20:00Z' : null,
    };
    fechamentos.push(f);

    const listaItens = ultimo ? config.itens : config.itens.map((i) => ({ ...i, status: 'ok', obs: '' }));
    listaItens.forEach((it, i) =>
      itens.push({ id: novoId('i'), fechamento_id: f.id, ordem: i, nome: it.nome, status: it.status, obs: it.obs || null })
    );

    const feitos = ultimo ? config.checklistFeitos : totalChecklist;
    for (let n = 1; n <= feitos; n++) {
      marcacoes.push({ fechamento_id: f.id, item_id: n, feito: true, feito_em: '2026-09-15T10:00:00Z' });
    }
  });
}

semear('e-rosa', ['2026-06-01', '2026-07-01'], {
  statusFinal: 'andamento', checklistFeitos: 8,
  itens: [
    { nome: 'Fiscal', status: 'ok' },
    { nome: 'Documentos bancários (Sicoob)', status: 'parcial', obs: 'Extrato de julho/2026 ainda não recebido do cliente.' },
    { nome: 'Balancete', status: 'pendente', obs: 'Aguardando documentos para poder fechar.' },
  ],
});

semear('e-andrade', ['2026-07-01', '2026-08-01'], {
  statusFinal: 'concluido', checklistFeitos: totalChecklist,
  itens: [{ nome: 'Fiscal', status: 'ok' }, { nome: 'Banco', status: 'ok' }],
});

semear('e-vitalle', ['2026-03-01', '2026-06-01'], {
  statusFinal: 'concluido', checklistFeitos: totalChecklist,
  itens: [{ nome: 'Fiscal', status: 'ok' }, { nome: 'Banco', status: 'ok' }, { nome: 'LALUR trimestral', status: 'ok' }],
});

semear('e-novorumo', ['2026-06-01', '2026-07-01'], {
  statusFinal: 'andamento', checklistFeitos: 11,
  itens: [
    { nome: 'Fiscal', status: 'ok' },
    { nome: 'Banco Sicoob', status: 'parcial', obs: 'Extrato de julho/2026 ainda não recebido — cliente já cobrado em 03/09.' },
  ],
});

semear('e-navegar', ['2026-07-01', '2026-08-01'], {
  statusFinal: 'andamento', checklistFeitos: 13,
  itens: [
    { nome: 'Banco Bradesco', status: 'ok' },
    { nome: 'Open Finance Caixa', status: 'pendente', obs: 'Adesão incompleta — banco ainda não homologou a conta.' },
  ],
});

semear('e-farias', ['2026-07-01', '2026-08-01'], {
  statusFinal: 'andamento', checklistFeitos: 14,
  itens: [
    { nome: 'Fiscal', status: 'ok' },
    { nome: 'Conciliação bancária', status: 'parcial', obs: '4 lançamentos sem identificação de centro de custo.' },
  ],
});

semear('e-cardoso', ['2026-08-01'], {
  statusFinal: 'andamento', checklistFeitos: 16,
  itens: [
    { nome: 'Fiscal', status: 'ok' },
    { nome: 'Conciliação de honorários', status: 'parcial', obs: 'Em andamento — 70% concluído.' },
  ],
});

semear('e-saopedro', ['2026-01-01', '2026-02-01', '2026-03-01'], {
  statusFinal: 'concluido', checklistFeitos: totalChecklist,
  itens: [{ nome: 'Fiscal', status: 'ok' }, { nome: 'Banco', status: 'ok' }],
});

semear('e-estetica', ['2026-08-01'], {
  statusFinal: 'andamento', checklistFeitos: 6,
  itens: [
    { nome: 'Fiscal', status: 'ok' },
    { nome: 'Classificação de despesas', status: 'parcial', obs: 'Em andamento — 40% concluído.' },
  ],
});

/* Documentos: alguns extratos faltando, o resto recebido. */
const COMPETENCIAS_DOC = ['2026-03-01', '2026-04-01', '2026-05-01', '2026-06-01', '2026-07-01', '2026-08-01', '2026-09-01'];
empresas.forEach((e) =>
  e.bancos.forEach((b) =>
    COMPETENCIAS_DOC.forEach((competencia, i) => {
      let status = 'recebido';
      if (!b.open_finance && i >= COMPETENCIAS_DOC.length - 2) status = 'faltante';
      else if (!b.open_finance && i === COMPETENCIAS_DOC.length - 3) status = 'parcial';
      else if (i === COMPETENCIAS_DOC.length - 1) status = 'analise';
      documentos.push({ id: novoId('d'), empresa_id: e.id, banco_id: b.id, competencia, status, obs: null });
    })
  )
);

const hoje = new Date();
const dataHoje = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;

/* Histórico inicial, refletindo os itens sinalizados. */
itens.filter((i) => i.status !== 'ok').forEach((i, n) => {
  const f = fechamentos.find((x) => x.id === i.fechamento_id);
  historico.push({
    id: 1000 + n,
    empresa_id: f.empresa_id,
    fechamento_id: f.id,
    competencia: f.competencia,
    tipo: 'item',
    descricao: i.nome,
    status_anterior: 'pendente',
    status_novo: i.status,
    // Hoje, para o realizado do dia mostrar o que veio do checklist.
    criado_em: dataHoje + 'T09:32:00Z',
    empresa: { id: f.empresa_id, nome: empresas.find((e) => e.id === f.empresa_id).nome },
    colaborador: ref('c-ademir'),
  });
});

/* Comentários de exemplo — a revisão da gestão sobre itens sinalizados. */
const itemFarias = itens.find((i) => i.nome === 'Conciliação bancária');
const itemRosa = itens.find((i) => i.nome === 'Documentos bancários (Sicoob)');
if (itemFarias) comentarios.push({
  id: 'cm-1', item_id: itemFarias.id, tipo: 'correcao',
  texto: 'Os 4 lançamentos sem centro de custo são da obra do Jabaquara. Antes de conciliar, puxe o relatório por obra — evita ter que refazer depois.',
  resolvido: false, resolvido_em: null, resolvedor: null,
  criado_em: '2026-09-16T11:05:00Z', autor: ref('c-beatriz'),
});
if (itemRosa) comentarios.push({
  id: 'cm-2', item_id: itemRosa.id, tipo: 'orientacao',
  texto: 'Esse cliente atrasa o Sicoob todo mês. Cobre no dia 5, não no dia 20 — assim dá tempo de fechar dentro do prazo.',
  resolvido: false, resolvido_em: null, resolvedor: null,
  criado_em: '2026-09-16T11:12:00Z', autor: ref('c-beatriz'),
});

/* ------------------------------------------------------------------ */
/* Operacional                                                         */
/* ------------------------------------------------------------------ */

const emp = (id) => ({ id, nome: empresas.find((e) => e.id === id).nome });

export const tarefas = [
  { id: novoId('t'), titulo: 'Conferir extratos Sicoob', empresa: emp('e-novorumo'), responsavel: ref('c-ademir'), prazo: '2026-09-17', prioridade: 'Alta', status: 'Pendente' },
  { id: novoId('t'), titulo: 'Reconfigurar Open Finance Caixa', empresa: emp('e-navegar'), responsavel: ref('c-cleytson'), prazo: '2026-09-18', prioridade: 'Alta', status: 'Em andamento' },
  { id: novoId('t'), titulo: 'Identificar 4 lançamentos bancários', empresa: emp('e-farias'), responsavel: ref('c-ademir'), prazo: '2026-09-19', prioridade: 'Média', status: 'Em andamento' },
  { id: novoId('t'), titulo: 'Solicitar contrato social atualizado', empresa: emp('e-rosa'), responsavel: ref('c-ademir'), prazo: '2026-09-22', prioridade: 'Baixa', status: 'Aguardando cliente' },
  { id: novoId('t'), titulo: 'Fechar balancete de agosto', empresa: emp('e-cardoso'), responsavel: ref('c-beatriz'), prazo: '2026-09-20', prioridade: 'Alta', status: 'Em andamento' },
  { id: novoId('t'), titulo: 'Classificar despesas de agosto', empresa: emp('e-estetica'), responsavel: ref('c-cleytson'), prazo: '2026-09-21', prioridade: 'Média', status: 'Em andamento' },
  { id: novoId('t'), titulo: 'Enviar DRE ao cliente', empresa: emp('e-vitalle'), responsavel: ref('c-cleytson'), prazo: '2026-09-15', prioridade: 'Baixa', status: 'Concluído' },
  { id: novoId('t'), titulo: 'Atualizar CON01 do mês', empresa: emp('e-andrade'), responsavel: ref('c-cleytson'), prazo: '2026-09-14', prioridade: 'Baixa', status: 'Concluído' },
];

export const pesquisas = [
  { id: novoId('p'), assunto: 'PER/DCOMP — prazo de compensação', empresa: emp('e-farias'), conclusao: 'Prazo de 5 anos contados do pagamento indevido.', fonte: 'Receita Federal', data: '2026-09-02', autor: ref('c-ademir') },
  { id: novoId('p'), assunto: 'AEA — quando usar conta de PL', empresa: null, conclusao: 'Erro em exercício encerrado e valor relevante: usar Lucros/Prejuízos Acumulados, não resultado.', fonte: 'Manual interno', data: '2026-08-21', autor: ref('c-beatriz') },
  { id: novoId('p'), assunto: 'Pró-labore sem movimento — impacto no INSS', empresa: emp('e-estetica'), conclusao: 'INSS incide mesmo sem saída bancária, se houve folha processada.', fonte: 'e-CAC', data: '2026-08-15', autor: ref('c-cleytson') },
];

export const kb = [
  { id: 1, nome: 'Open Finance', ordem: 1, artigos: [
    { id: 'k1', titulo: 'Como reconfigurar uma adesão incompleta' },
    { id: 'k2', titulo: 'Layouts aceitos por banco (CNAB240 / OFX)' }] },
  { id: 2, nome: 'Extratos', ordem: 2, artigos: [
    { id: 'k3', titulo: 'Importação de extratos ou contabilidade digital' },
    { id: 'k4', titulo: 'O que fazer quando o extrato não bate' }] },
  { id: 3, nome: 'Conciliação', ordem: 3, artigos: [
    { id: 'k5', titulo: 'Lançamentos sem identificação bancária' },
    { id: 'k6', titulo: 'Conciliação de fornecedores e clientes' }] },
  { id: 4, nome: 'Balancete/Balanço', ordem: 4, artigos: [
    { id: 'k7', titulo: 'Estrutura do balancete de referência' },
    { id: 'k8', titulo: 'Como usar a Composição de Saldos' }] },
  { id: 5, nome: 'Documentos', ordem: 5, artigos: [
    { id: 'k9', titulo: 'Organização de documentos na pasta do cliente' },
    { id: 'k10', titulo: 'Cobrança padrão de documentos faltantes' }] },
  { id: 6, nome: 'Procedimentos internos', ordem: 6, artigos: [
    { id: 'k11', titulo: 'Checklist padrão de fechamento mensal' },
    { id: 'k12', titulo: 'Protocolo de decisão antes de tratamento novo' }] },
  { id: 7, nome: 'Outros', ordem: 7, artigos: [
    { id: 'k13', titulo: 'Canais de consulta — IOB, Legisweb, Domínio' }] },
];


export const realizado = [
  { id: novoId('r'), data: dataHoje, hora: '08:20', atividade: 'Conferência de extratos', status: 'Concluído', empresa: emp('e-novorumo'), empresa_texto: null, colaborador: ref('c-beatriz') },
  { id: novoId('r'), data: dataHoje, hora: '09:15', atividade: 'Solicitação de documentos', status: 'Pendente', empresa: emp('e-rosa'), empresa_texto: null, colaborador: ref('c-beatriz') },
  { id: novoId('r'), data: dataHoje, hora: '10:30', atividade: 'Conciliação bancária', status: 'Concluído', empresa: emp('e-farias'), empresa_texto: null, colaborador: ref('c-beatriz') },
  { id: novoId('r'), data: dataHoje, hora: '11:45', atividade: 'Pesquisa contábil', status: 'Concluído', empresa: emp('e-cardoso'), empresa_texto: null, colaborador: ref('c-beatriz') },
  { id: novoId('r'), data: dataHoje, hora: '14:20', atividade: 'Lançamento de documentos', status: 'Pendente', empresa: emp('e-estetica'), empresa_texto: null, colaborador: ref('c-ademir') },
];

/* ------------------------------------------------------------------ */
/* Consultas e escritas (tudo em memória)                              */
/* ------------------------------------------------------------------ */

export function empresasComSituacao() {
  return empresas.map((e) => {
    const meus = fechamentos.filter((f) => f.empresa_id === e.id);
    const abertos = meus.filter((f) => f.status !== 'concluido');
    const concluidos = meus.filter((f) => f.status === 'concluido');
    const pendencias = abertos.reduce(
      (n, f) => n + itens.filter((i) => i.fechamento_id === f.id && i.status !== 'ok').length, 0
    );
    return {
      ...e,
      pendencias,
      competencia_aberta: abertos.length ? abertos.map((f) => f.competencia).sort()[0] : null,
      realizado_ate: concluidos.length ? concluidos.map((f) => f.competencia).sort().at(-1) : null,
    };
  });
}

export function resumos() {
  return fechamentos.map((f) => ({
    fechamento_id: f.id,
    empresa_id: f.empresa_id,
    competencia: f.competencia,
    status: f.status,
    itens_total: itens.filter((i) => i.fechamento_id === f.id).length,
    itens_pendentes: itens.filter((i) => i.fechamento_id === f.id && i.status === 'pendente').length,
    itens_parciais: itens.filter((i) => i.fechamento_id === f.id && i.status === 'parcial').length,
    checklist_feitos: marcacoes.filter((m) => m.fechamento_id === f.id && m.feito).length,
    checklist_total: totalChecklist,
  }));
}

export function sinalizados() {
  return itens
    .filter((i) => i.status !== 'ok')
    .map((i) => {
      const f = fechamentos.find((x) => x.id === i.fechamento_id);
      if (!f || f.status === 'concluido') return null;
      const e = empresas.find((x) => x.id === f.empresa_id);
      return {
        id: i.id, nome: i.nome, status: i.status, obs: i.obs,
        fechamento: {
          id: f.id, competencia: f.competencia, status: f.status,
          empresa: {
            id: e.id, nome: e.nome,
            responsaveis: e.responsaveis.map((r) => ({ colaborador: r })),
          },
        },
      };
    })
    .filter(Boolean);
}

export function registrarHistorico({ empresaId, fechamentoId, competencia, tipo, descricao, anterior, novo }) {
  const e = empresas.find((x) => x.id === empresaId);
  historico.unshift({
    id: Date.now(),
    empresa_id: empresaId, fechamento_id: fechamentoId, competencia, tipo, descricao,
    status_anterior: anterior, status_novo: novo,
    criado_em: new Date().toISOString(),
    empresa: e ? { id: e.id, nome: e.nome } : null,
    colaborador: ref(usuarioDemo.id),
  });
}
